'use client'

import { useState, useEffect, useRef, useMemo } from 'react'
import { createClient } from '~/lib/supabase/client'
import Image from 'next/image'
import Link from 'next/link'
import { resolveImageUrl } from '~/lib/images'
import { Users, User, Loader2, MapPin, ChevronDown, X, Search as SearchIcon } from 'lucide-react'
import { env } from '~/env'
import { VisitorEmptyState } from '~/components/auth/VisitorEmptyState'
import { AuthPromptDialog } from '~/components/auth/AuthPromptDialog'
import { scoreFacilitatorsByPrompt } from '~/lib/facilitator-prompt-score'
import { logSearch } from '~/lib/activity-tracking'

type Facilitator = {
  user_id: string
  full_name: string
  email: string
  avatar_url: string | null
  approval_levels?: string[]
  professional_title?: string
  state?: string | null    // abbreviated, e.g. "VIC"
  suburb?: string | null   // city, e.g. "Cleveland"
  languages?: string[]
  primary_focus?: string | null
  primary_focus_areas?: string[]
  profile_tags?: string[]
  short_bio?: string | null
  qualifications_text?: string | null
  stage?: 'ONBOARDING' | 'NEW' | 'ENGAGED'
}

// Derived host lifecycle stage → member-facing label
const STAGE_LABELS: Record<string, string> = {
  NEW: 'Emerging host',
  ENGAGED: 'Engaged host',
  // ONBOARDING = approved but no published events yet (see facilitator-stage.ts).
  // Renamed from INFLOW so neither the value nor the label echoes the retired
  // "In Flow Host" badge name (#20), which meant the opposite (top tier).
  ONBOARDING: 'Onboarding host',
}

// Map approval levels to role names
function getRoleFromApprovalLevel(approvalLevel: string): string {
  const roleMap: Record<string, string> = {
    'COMMUNITY_HOST': 'Community Host',
    'WELLNESS_GUIDE': 'Wellness Guide',
    'HOLISTIC_PRACTITIONER': 'Holistic Practitioner',
  }
  return roleMap[approvalLevel] || ''
}

function getFacilitatorRole(facilitator: Facilitator): string {
  if (facilitator.professional_title) return facilitator.professional_title
  if (facilitator.approval_levels && facilitator.approval_levels.length > 0) {
    const firstLevel = facilitator.approval_levels[0]
    if (firstLevel) {
      const role = getRoleFromApprovalLevel(firstLevel)
      if (role) return role
    }
  }
  return 'Facilitator'
}

function getDisplayState(facilitator: Facilitator): string | null {
  return facilitator.state ?? null
}

function getDisplayLocation(facilitator: Facilitator): string | null {
  if (facilitator.suburb && facilitator.state) return `${facilitator.suburb}, ${facilitator.state}`
  return facilitator.state ?? null
}

// --- Searchable Filter Component ---
function SearchableFilter({
  label,
  placeholder,
  value,
  onChange,
  options,
}: {
  label: string
  placeholder: string
  value: string
  onChange: (value: string) => void
  options: string[]
}) {
  const [query, setQuery] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const filtered = useMemo(() => {
    if (!query) return options
    const lower = query.toLowerCase()
    return options.filter((o) => o.toLowerCase().includes(lower))
  }, [query, options])

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const handleSelect = (option: string) => {
    onChange(option)
    setQuery('')
    setIsOpen(false)
  }

  const handleClear = () => {
    onChange('')
    setQuery('')
    setIsOpen(false)
  }

  return (
    <div ref={containerRef} className="relative">
      <label className="block text-sm font-medium text-neutral-emphasis mb-2">
        {label}
      </label>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          placeholder={value || placeholder}
          value={isOpen ? query : ''}
          onChange={(e) => {
            setQuery(e.target.value)
            if (!isOpen) setIsOpen(true)
          }}
          onFocus={() => setIsOpen(true)}
          className={`w-full px-3 py-2 pr-16 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#DE866D] focus:border-transparent text-sm ${value && !isOpen ? 'text-neutral-emphasis' : ''
            }`}
        />
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {value && (
            <button
              type="button"
              onClick={handleClear}
              className="p-0.5 rounded hover:bg-[#F7F0E6]"
            >
              <X className="h-3.5 w-3.5 text-[#5E594F]" />
            </button>
          )}
          <ChevronDown className={`h-4 w-4 text-[#5E594F] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </div>
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full max-h-48 overflow-y-auto bg-[#FFFDF8] border border-[#D9CDB9] rounded-lg shadow-lg">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-[#5E594F]">No matches</div>
          ) : (
            filtered.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => handleSelect(option)}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-[#F6E1DA] transition-colors ${value === option ? 'bg-[#F6E1DA] font-medium text-[#DE866D]' : 'text-neutral-emphasis'
                  }`}
              >
                {option}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}

// --- Facilitator Name Filter (with avatars, matching Discover page style) ---
function FacilitatorNameFilter({
  value,
  onChange,
  facilitators,
}: {
  value: string
  onChange: (value: string) => void
  facilitators: Facilitator[]
}) {
  const [query, setQuery] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const filtered = useMemo(() => {
    if (!query) return facilitators
    const lower = query.toLowerCase()
    return facilitators.filter((f) => f.full_name.toLowerCase().includes(lower))
  }, [query, facilitators])

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const handleSelect = (name: string) => {
    onChange(name)
    setQuery('')
    setIsOpen(false)
  }

  const handleClear = () => {
    onChange('')
    setQuery('')
    setIsOpen(false)
  }

  return (
    <div ref={containerRef} className="relative">
      <label className="block text-sm font-medium text-neutral-emphasis mb-2">
        Name
      </label>
      <div className="relative">
        <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#5E594F] pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          placeholder={value || 'Search facilitator...'}
          value={isOpen ? query : ''}
          onChange={(e) => {
            setQuery(e.target.value)
            if (!isOpen) setIsOpen(true)
          }}
          onFocus={() => setIsOpen(true)}
          className={`w-full pl-10 pr-16 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#DE866D] focus:border-transparent text-sm ${value && !isOpen ? 'text-neutral-emphasis' : ''
            }`}
        />
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {value && (
            <button
              type="button"
              onClick={handleClear}
              className="p-0.5 rounded hover:bg-[#F7F0E6]"
            >
              <X className="h-3.5 w-3.5 text-[#5E594F]" />
            </button>
          )}
        </div>
      </div>
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full max-h-60 overflow-y-auto bg-[#FFFDF8] border border-[#D9CDB9] rounded-lg shadow-lg">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-[#5E594F]">No matches</div>
          ) : (
            filtered.map((f) => (
              <button
                key={f.user_id}
                type="button"
                onClick={() => handleSelect(f.full_name)}
                className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-[#F7F0E6] transition-colors text-left ${value === f.full_name ? 'bg-[#F6E1DA]' : ''
                  }`}
              >
                {f.avatar_url ? (
                  <div className="relative w-10 h-10 flex-shrink-0">
                    <Image
                      src={resolveImageUrl(f.avatar_url)}
                      alt={f.full_name}
                      fill
                      className="rounded-full object-cover"
                      unoptimized
                    />
                  </div>
                ) : (
                  <div className="w-10 h-10 bg-neutral-200 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-[#2C2D2B] font-medium text-sm">
                      {f.full_name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                <span className={`text-sm font-medium ${value === f.full_name ? 'text-[#DE866D]' : 'text-[#2C2D2B]'
                  }`}>
                  {f.full_name}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}

export default function FacilitatorProfileListPage() {
  const [facilitators, setFacilitators] = useState<Facilitator[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isGuest, setIsGuest] = useState(false)
  const [showAuthPrompt, setShowAuthPrompt] = useState(false)

  // Filter states
  const [nameFilter, setNameFilter] = useState('')
  const [stateFilter, setStateFilter] = useState('')
  const [languageFilter, setLanguageFilter] = useState('')
  const [stageFilter, setStageFilter] = useState('')
  const [smartPrompt, setSmartPrompt] = useState('')
  const [accessToken, setAccessToken] = useState<string | null>(null)

  // Compute filter options from loaded facilitators
  const stateOptions = useMemo(() => {
    const states = new Set<string>()
    facilitators.forEach((f) => {
      const s = getDisplayState(f)
      if (s) states.add(s)
    })
    return Array.from(states).sort()
  }, [facilitators])

  const languageOptions = useMemo(() => {
    const languages = new Set<string>()
    facilitators.forEach((f) => (f.languages ?? []).forEach((l) => l && languages.add(l)))
    return Array.from(languages).sort()
  }, [facilitators])

  const stageOptions = useMemo(() => {
    const stages = new Set<string>()
    facilitators.forEach((f) => {
      if (f.stage && STAGE_LABELS[f.stage]) stages.add(STAGE_LABELS[f.stage]!)
    })
    return Array.from(stages).sort()
  }, [facilitators])

  useEffect(() => {
    async function fetchFacilitators() {
      try {
        setIsLoading(true)
        setError(null)

        const supabase = createClient()
        const { data: sessionData } = await supabase.auth.getSession()
        const accessToken = sessionData?.session?.access_token
        setAccessToken(accessToken ?? null)

        // Visitors see the page structure (filters) but no member data —
        // the API requires auth for this list, so don't even fetch.
        if (!accessToken) {
          setIsGuest(true)
          setShowAuthPrompt(true)
          setIsLoading(false)
          return
        }

        const headers: HeadersInit = {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        }

        // Fetch approved facilitators from dedicated endpoint
        const facilitatorsRes = await fetch(`${env.NEXT_PUBLIC_API_URL}/user/facilitators`, {
          headers,
          cache: 'no-store',
        })

        if (facilitatorsRes.ok) {
          const facilitatorsList = await facilitatorsRes.json()
        
          setFacilitators(
            facilitatorsList.map((f: any) => ({
              user_id: f.user_id,
              full_name: f.full_name,
              email: '',
              avatar_url: f.avatar_url,
              approval_levels: f.approval_levels || [],
              professional_title: f.professional_title,
              state: f.state ?? null,
              suburb: f.suburb ?? null,
              languages: f.languages ?? [],
              primary_focus: f.primary_focus ?? null,
              primary_focus_areas: f.primary_focus_areas ?? [],
              profile_tags: f.profile_tags ?? [],
              short_bio: f.short_bio ?? null,
              qualifications_text: f.qualifications_text ?? null,
              stage: f.stage,
            }))
          )
        } else {
          console.error('Failed to load facilitators:', facilitatorsRes.status)
          setError('Failed to load facilitators. Please try again later.')
        }
      } catch (err) {
        console.error('Error fetching facilitators:', err)
        setError('Failed to load facilitators. Please try again later.')
      } finally {
        setIsLoading(false)
      }
    }

    void fetchFacilitators()
  }, [])

  // Apply filters, then smart-prompt scoring (same pattern as Discover)
  const filteredFacilitators = useMemo(() => {
    let result = facilitators.filter((facilitator) => {
      if (nameFilter && facilitator.full_name !== nameFilter) return false
      if (stateFilter && getDisplayState(facilitator) !== stateFilter) return false
      if (languageFilter && !(facilitator.languages ?? []).includes(languageFilter)) return false
      if (stageFilter && STAGE_LABELS[facilitator.stage ?? ''] !== stageFilter) return false
      return true
    })
    if (smartPrompt.trim()) {
      result = scoreFacilitatorsByPrompt(result, smartPrompt)
        .sort((a, b) => b.score - a.score)
        .map((x) => x.facilitator)
    }
    return result
  }, [facilitators, nameFilter, stateFilter, languageFilter, stageFilter, smartPrompt])

  // Log directory searches (debounced) so they feed the unified match scorer,
  // mirroring Discovery. page_source distinguishes them from Discovery searches.
  useEffect(() => {
    if (!accessToken) return
    const query = smartPrompt.trim()
    const activeFilters = { nameFilter, stateFilter, languageFilter, stageFilter }
    const hasFilters = Object.values(activeFilters).some(Boolean)
    if (!query && !hasFilters) return
    const timer = setTimeout(() => {
      void logSearch({
        query,
        filters: hasFilters ? activeFilters : undefined,
        resultsCount: filteredFacilitators.length,
        pageSource: 'facilitator_page',
        accessToken,
      })
    }, 1500)
    return () => clearTimeout(timer)
    // filteredFacilitators.length intentionally omitted — log when the search
    // settles, not on every list change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [smartPrompt, nameFilter, stateFilter, languageFilter, stageFilter, accessToken])

  return (
    <div className="min-h-screen bg-background">
      <AuthPromptDialog
        open={showAuthPrompt}
        onOpenChange={setShowAuthPrompt}
        title="Discover our facilitator community"
        description="Create a free account to explore facilitators and connect with the community."
        icon="🌿"
      />
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="fixed bg-[#F7F0E6]/70 backdrop-blur-md z-25  top-15 pt-2 pb-2 sm:pt-0 sm:top-0 sm:relative flex items-center justify-between mb-6 w-full">
            <div className='p-3'>
              <h1 className="text-3xl font-bold text-neutral-emphasis mb-2 flex items-center gap-3">
                <Users className="h-8 w-8 text-[#DE866D]" />
                Facilitators
              </h1>
              <p className="text-neutral">
                Browse all our wellness facilitators and discover their unique offerings.
              </p>
            </div>
          </div>

        </div>

        <div className="pt-31 sm:pt-0 max-w-7xl mx-auto space-y-10">

          {/* Filter Section */}
          <div className="bg-[#FFFDF8] rounded-[20px] border border-[#D9CDB9] p-6 mb-6">
            <h2 className="text-lg font-semibold text-neutral-emphasis mb-4">Filters</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Name Filter */}
              <FacilitatorNameFilter
                value={nameFilter}
                onChange={setNameFilter}
                facilitators={facilitators}
              />

              {/* Location Filter (by state) */}
              <SearchableFilter
                label="Location"
                placeholder="All states"
                value={stateFilter}
                onChange={setStateFilter}
                options={stateOptions}
              />

              {/* Language Filter */}
              <SearchableFilter
                label="Language"
                placeholder="All languages"
                value={languageFilter}
                onChange={setLanguageFilter}
                options={languageOptions}
              />

              {/* Host Stage Filter */}
              <SearchableFilter
                label="Host stage"
                placeholder="All stages"
                value={stageFilter}
                onChange={setStageFilter}
                options={stageOptions}
              />
            </div>

            {/* Smart search over offerings (same UX as Discover) */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-neutral-emphasis mb-2">
                Search by offering
              </label>
              <div className="relative">
                <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#5E594F] pointer-events-none" />
                <input
                  type="text"
                  placeholder="e.g. breathwork for stress, sound healing, grief support..."
                  value={smartPrompt}
                  onChange={(e) => setSmartPrompt(e.target.value)}
                  className="w-full pl-10 pr-10 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#DE866D] focus:border-transparent text-sm"
                />
                {smartPrompt && (
                  <button
                    type="button"
                    onClick={() => setSmartPrompt('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-[#F7F0E6]"
                  >
                    <X className="h-3.5 w-3.5 text-[#5E594F]" />
                  </button>
                )}
              </div>
            </div>

            {/* Results Count — hidden for visitors */}
            {!isGuest && (
              <div className="mt-4 text-sm text-neutral">
                Showing {filteredFacilitators.length} of {facilitators.length} facilitators
              </div>
            )}
          </div>

          {/* Visitor State — structure stays visible, results area invites sign-up */}
          {!isLoading && isGuest && (
            <VisitorEmptyState
              title="Discover our facilitator community"
              description="Create a free account to explore facilitators and connect with the community."
            />
          )}

          {/* Loading State */}
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[#DE866D]" />
              <span className="ml-3 text-neutral">Loading facilitators...</span>
            </div>
          )}

          {/* Error State */}
          {error && !isLoading && (
            <div className="bg-[#FFFDF8] rounded-xl border border-border p-12 text-center">
              <Users className="h-16 w-16 mx-auto text-red-300 mb-4" />
              <h3 className="text-lg font-semibold text-neutral-emphasis mb-2">
                Error Loading Facilitators
              </h3>
              <p className="text-neutral">{error}</p>
            </div>
          )}

          {/* Facilitators Grid */}
          {!isLoading && !error && filteredFacilitators.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredFacilitators.map((facilitator) => {
                const locationString = getDisplayLocation(facilitator)

                return (
                  <Link
                    key={facilitator.user_id}
                    href={`/facilitator-profile/${facilitator.user_id}`}
                    className="bg-[#FFFDF8] rounded-[20px] border border-[#D9CDB9] hover:border-[#75A096] transition-all duration-200 overflow-hidden group"
                  >
                    <div className="p-6">
                      {/* Avatar */}
                      <div className="flex flex-col items-center mb-4">
                        <div className="relative flex-shrink-0 mb-3">
                          {facilitator.avatar_url ? (
                            <div className="relative w-20 h-20 rounded-full overflow-hidden">
                              <Image
                                src={resolveImageUrl(facilitator.avatar_url)}
                                alt={facilitator.full_name}
                                fill
                                className="object-cover"
                                unoptimized
                              />
                            </div>
                          ) : (
                            <div className="w-20 h-20 bg-[#F6E1DA] rounded-full flex items-center justify-center">
                              <User className="h-10 w-10 text-[#DE866D]" />
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Name */}
                      <div className="mb-2">
                        <h3 className="font-semibold text-neutral-emphasis text-lg group-hover:text-[#DE866D] transition-colors text-center">
                          {facilitator.full_name}
                        </h3>
                      </div>

                      {/* Professional Title */}
                      <div className="mb-3">
                        <p className="text-neutral text-sm text-center">
                          {getFacilitatorRole(facilitator)}
                        </p>
                        {facilitator.stage && STAGE_LABELS[facilitator.stage] && (
                          <p className="mt-1.5 text-center">
                            <span className="inline-block rounded-full bg-[#F6E1DA] px-2.5 py-0.5 text-xs font-medium text-[#b06046]">
                              {STAGE_LABELS[facilitator.stage]}
                            </span>
                          </p>
                        )}
                      </div>

                      {/* Location */}
                      {locationString && (
                        <div className="flex items-center gap-2 text-neutral justify-center">
                          <MapPin className="h-4 w-4 flex-shrink-0 text-neutral-500" />
                          <span className="text-sm truncate">{locationString}</span>
                        </div>
                      )}
                    </div>
                  </Link>
                )
              })}
            </div>
          )}

          {/* No Results After Filtering */}
          {!isLoading && !error && facilitators.length > 0 && filteredFacilitators.length === 0 && (
            <div className="bg-[#FFFDF8] rounded-xl border border-border p-12 text-center">
              <Users className="h-16 w-16 mx-auto text-neutral-300 mb-4" />
              <h3 className="text-lg font-semibold text-neutral-emphasis mb-2">
                No Matching Facilitators
              </h3>
              <p className="text-neutral">
                No facilitators match your current filters. Try adjusting your search criteria.
              </p>
            </div>
          )}

          {/* Empty State */}
          {!isLoading && !error && !isGuest && facilitators.length === 0 && (
            <div className="bg-[#FFFDF8] rounded-xl border border-border p-12 text-center">
              <Users className="h-16 w-16 mx-auto text-neutral-300 mb-4" />
              <h3 className="text-lg font-semibold text-neutral-emphasis mb-2">
                No Facilitators Found
              </h3>
              <p className="text-neutral">
                There are no facilitators available at the moment. Please check back later.
              </p>
            </div>
          )}

        </div>
      </div>
    </div>
  )
}
